import { TOKEN_KEY, REFRESH_TOKEN_KEY } from "../api/authKeys";
import { refreshSession } from "../api/client";
import { getAuthValue } from "./authStorage";

const REFRESH_SKEW_MS = 10 * 60 * 1000;
const KEEP_ALIVE_INTERVAL_MS = 5 * 60 * 1000;

let keepAliveTimer = null;
let keepAliveInFlight = null;

export function getAccessTokenExpiry(token) {
  if (!token || typeof token !== "string") return 0;
  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return 0;
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const payload = JSON.parse(atob(padded));
    return Number(payload?.exp) > 0 ? Number(payload.exp) * 1000 : 0;
  } catch {
    return 0;
  }
}

export function shouldRefreshAccessToken(token) {
  if (!token) return true;
  const expiresAt = getAccessTokenExpiry(token);
  if (!expiresAt) return true;
  return expiresAt - Date.now() <= REFRESH_SKEW_MS;
}

export async function silentRefreshIfNeeded({ force = false } = {}) {
  const refreshToken = getAuthValue(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;

  const accessToken = getAuthValue(TOKEN_KEY);
  if (!force && !shouldRefreshAccessToken(accessToken)) return true;

  if (keepAliveInFlight) return keepAliveInFlight;

  keepAliveInFlight = (async () => {
    try {
      await refreshSession({ logoutOnAuthFailure: false });
      return true;
    } catch {
      // Network errors must not log the user out. A real invalid session is
      // handled later by the API interceptor when a protected call 401s.
      return false;
    } finally {
      keepAliveInFlight = null;
    }
  })();

  return keepAliveInFlight;
}

export function startSessionKeepAlive() {
  stopSessionKeepAlive();

  const tick = () => {
    silentRefreshIfNeeded().catch(() => {});
  };

  tick();
  keepAliveTimer = setInterval(tick, KEEP_ALIVE_INTERVAL_MS);

  const onResume = () => tick();
  const onVisible = () => {
    if (document.visibilityState === "visible") tick();
  };

  window.addEventListener("app:resume", onResume);
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    stopSessionKeepAlive();
    window.removeEventListener("app:resume", onResume);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

export function stopSessionKeepAlive() {
  if (keepAliveTimer) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
}
